import React, { useState, useMemo, FormEvent } from 'react';
import { Modal, Button, Alert, Table, Checkbox } from 'react-bootstrap';
import { IContactForList } from '@nkr/models/index';
import { BulkDeleteTableWrapper } from '../styles';

interface IProps {
    show: boolean;
    data: IContactForList[];
    selectedIds: number[];
    onSelectionChange: (selectedIds: number[]) => void;
    onDelete: () => void;
    onCancel: () => void;
}

const BulkDelete: React.FC<IProps> = ({ show, data, selectedIds, onSelectionChange, onDelete, onCancel }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredContacts = useMemo(() => {
        if (!searchTerm.trim()) {
            return data;
        }

        const term = searchTerm.toLowerCase();

        return data.filter(contact =>
            contact.firstName?.toLowerCase().includes(term)
            || contact.lastName?.toLowerCase().includes(term)
            || contact.email?.toLowerCase().includes(term)
        );
    }, [data, searchTerm]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const filteredIds = filteredContacts.map(contact => contact.id);
            onSelectionChange(filteredIds);
        } else {
            const filteredIds = filteredContacts.map(contact => contact.id);
            onSelectionChange(selectedIds.filter(id => !filteredIds.includes(id)));
        }
    };

    const handleSelectContact = (contactId: number, checked: boolean) => {
        if (checked) {
            onSelectionChange([...selectedIds, contactId]);
        } else {
            onSelectionChange(selectedIds.filter(id => id !== contactId));
        }
    };

    const isAllSelected = filteredContacts.length > 0 && filteredContacts.every(contact => selectedIds.includes(contact.id));

    const isIndeterminate = filteredContacts.some(contact => selectedIds.includes(contact.id)) && !filteredContacts.every(contact => selectedIds.includes(contact.id));

    const selectedFromFiltered = filteredContacts.filter(contact => selectedIds.includes(contact.id)).length;

    return (
        <Modal
            show={show}
            onHide={onCancel}
            backdrop="static"
            keyboard={false}
            dialogClassName="modal-xl"
            style={{
                width: '100vw',
                maxWidth: '1400px',
                minWidth: '1950px',
                direction: 'ltr'
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Bulk Hard Delete Contacts</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Alert bsStyle="danger">
                    <strong>Warning:</strong> This action will permanently delete the selected contact(s) and cannot be undone.
                </Alert>

                <div style={{ marginBottom: 15 }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />

                    <Checkbox
                        checked={isAllSelected}
                        onChange={(e: FormEvent<Checkbox>) => handleSelectAll((e.target as HTMLInputElement).checked)}
                    >
                        <strong>
                            Select All ({filteredContacts.length} contacts)
                            {searchTerm && ` - ${selectedFromFiltered} selected`}
                        </strong>
                    </Checkbox>
                </div>

                {filteredContacts.length > 0 ? (
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: '300px' }}>
                        <BulkDeleteTableWrapper striped bordered condensed hover responsive>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                <tr>
                                    <th>R#</th>
                                    <th style={{ width: '50px' }}>Select</th>
                                    <th style={{ width: '200px' }}>Name</th>
                                    <th style={{ width: '250px' }}>Email</th>
                                    <th style={{ width: '150px' }}>Entity Type</th>
                                    <th style={{ width: '100px' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredContacts.map((contact, index) => (
                                    <tr
                                        key={contact.id}
                                        className={contact.isDeleted ? 'danger' : ''}
                                    >
                                        <td>{index + 1}</td>
                                        <td>
                                            <Checkbox
                                                checked={selectedIds.includes(contact.id)}
                                                onChange={(e: FormEvent<Checkbox>) => handleSelectContact(contact.id, (e.target as HTMLInputElement).checked)}
                                            />
                                        </td>
                                        <td>
                                            {contact.firstName} {contact.lastName}
                                            {contact.isDeleted && (
                                                <span className="label label-danger" style={{ marginLeft: 5 }}>
                                                    DELETED
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ wordBreak: 'break-word' }}>{contact.email}</td>
                                        <td>{contact.entityTypeName}</td>
                                        <td>
                                            {contact.isDeleted ? (
                                                <span className="label label-danger">Deleted</span>
                                            ) : (
                                                <span className="label label-success">Active</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </BulkDeleteTableWrapper>
                    </div>
                ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p>
                            {searchTerm ? 'No contacts found matching your search.' : 'No contacts available for selection.'}
                        </p>
                    </div>
                )}

                {selectedIds.length > 0 && (
                    <Alert bsStyle="warning" style={{ marginTop: 15, marginBottom: 0 }}>
                        <strong>Ready to delete:</strong> {selectedIds.length} contact(s) selected
                        {searchTerm && (
                            <span> (from {filteredContacts.length} filtered results)</span>
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    bsStyle="danger"
                    onClick={onDelete}
                    disabled={selectedIds.length === 0}
                >
                    Permanently Delete {selectedIds.length} Contact(s)
                </Button>
                <Button bsStyle="default" onClick={onCancel}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkDelete;